import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { analytics, AnalyticsEvent } from "../lib/analytics";
import { trackApiError } from "../lib/analytics/errorTracking";
import { FixturesPanel } from "./admin/FixturesPanel";
import { SheetMirrorPanel } from "./admin/SheetMirrorPanel";
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  ErrorNote,
  Icon,
  InfoNote,
  Input,
  Modal,
  PageHeader,
  SegmentedControl,
  Select,
  Skeleton,
  StatTile,
  SuccessNote,
  Tabs,
  Textarea,
  cx,
  formatKickoff,
  formatRelative,
  useToast,
  type IconName,
} from "../components/ui";

type AdminTab =
  | "overview"
  | "users"
  | "competitions"
  | "fixtures"
  | "sync"
  | "providers"
  | "import"
  | "backup"
  | "audit";

const ADMIN_TABS: Array<{ value: AdminTab; label: string; icon: IconName }> = [
  { value: "overview", label: "Overview", icon: "grid" },
  { value: "users", label: "Users", icon: "users" },
  { value: "competitions", label: "Competitions", icon: "trophy" },
  { value: "fixtures", label: "Fixtures", icon: "calendar" },
  { value: "sync", label: "Sync", icon: "refresh" },
  { value: "providers", label: "Providers", icon: "link" },
  { value: "import", label: "Import", icon: "download" },
  { value: "backup", label: "Backup", icon: "copy" },
  { value: "audit", label: "Audit log", icon: "list" },
];

/**
 * Super admin. Everything here is audited, including these actions themselves — that's what
 * makes an override defensible when a player asks why they went out.
 */
export function AdminPage() {
  const [tab, setTab] = useState<AdminTab>("overview");

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Admin"
        subtitle="Full control over users, competitions, providers and fixture data across the platform."
      />

      <div className="under-header mb-6 bg-canvas/95 backdrop-blur-sm">
        <Tabs
          value={tab}
          onChange={(next) => {
            analytics.track(AnalyticsEvent.AdminTabViewed, { tab: next });
            setTab(next);
          }}
          tabs={ADMIN_TABS}
        />
      </div>

      <div key={tab} className="animate-fade">
        {tab === "overview" && <OverviewPanel />}
        {tab === "users" && <UsersPanel />}
        {tab === "competitions" && <CompetitionsPanel />}
        {tab === "fixtures" && <FixturesPanel />}
        {tab === "sync" && <SyncPanel />}
        {tab === "providers" && <ProvidersPanel />}
        {tab === "import" && <ImportPanel />}
        {tab === "backup" && <SheetMirrorPanel />}
        {tab === "audit" && <AuditPanel />}
      </div>
    </>
  );
}

/* Overview ----------------------------------------------------------------- */

function OverviewPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => api.admin.overview(),
  });

  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6">
      {data.failedSyncs24h > 0 && (
        <ErrorNote>
          {data.failedSyncs24h} sync {data.failedSyncs24h === 1 ? "run has" : "runs have"} failed
          in the last 24 hours. Check the Sync tab — a silent provider failure is worse than a
          loud one.
        </ErrorNote>
      )}

      <div className="stagger grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile label="Users" value={data.users} />
        <StatTile label="Competitions" value={data.competitions} />
        <StatTile label="Running" value={data.runningCompetitions} tone="alive" />
        <StatTile label="Players alive" value={data.aliveEntries} tone="alive" />
        <StatTile label="Active leagues" value={data.leagues} />
      </div>
    </div>
  );
}

/* Users -------------------------------------------------------------------- */

const PAGE_SIZE = 25;

function Pager({
  offset,
  total,
  onChange,
}: {
  offset: number;
  total: number;
  onChange: (next: number) => void;
}) {
  if (total <= PAGE_SIZE) return null;
  return (
    <div className="flex items-center justify-between gap-3">
      <Button
        size="sm"
        variant="secondary"
        disabled={offset === 0}
        onClick={() => onChange(Math.max(0, offset - PAGE_SIZE))}
      >
        <Icon name="chevronLeft" className="size-4" /> Previous
      </Button>
      <p className="tabular text-[13px] text-fg-subtle">
        {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
      </p>
      <Button
        size="sm"
        variant="secondary"
        disabled={offset + PAGE_SIZE >= total}
        onClick={() => onChange(offset + PAGE_SIZE)}
      >
        Next <Icon name="chevronRight" className="size-4" />
      </Button>
    </div>
  );
}

function UsersPanel() {
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [detailId, setDetailId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "users", search, offset],
    queryFn: () => api.admin.users({ search: search || undefined, limit: PAGE_SIZE, offset }),
  });

  const toggleSuperAdmin = useMutation({
    mutationFn: ({ id, grant }: { id: string; grant: boolean }) =>
      api.admin.setSuperAdmin(id, grant),
    onSuccess: (_result, { id, grant }) => {
      analytics.track(AnalyticsEvent.AdminSuperAdminToggled, { userId: id, grant });
      toast.show(grant ? "Super admin granted" : "Super admin revoked");
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "user", detailId] });
    },
    onError: (error) => trackApiError(error, "toggle_super_admin"),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOffset(0);
          }}
          placeholder="Search by username or email…"
          icon="search"
          aria-label="Search users"
          className="max-w-sm flex-1"
        />
        {data && (
          <p className="text-[13px] text-fg-subtle">
            {data.total} {data.total === 1 ? "user" : "users"}
          </p>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !data?.users.length ? (
        <EmptyState title="No users found" description="Try a different search." />
      ) : (
        <div className="card divide-y divide-line overflow-hidden">
          {data.users.map((user) => (
            <div key={user.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
                onClick={() => setDetailId(user.id)}
              >
                <Avatar name={user.displayName} color={user.avatarColor} size={36} />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-fg">
                      {user.displayName}
                    </span>
                    {user.isSuperAdmin && <Badge tone="warn">Super admin</Badge>}
                  </span>
                  <span className="block truncate text-[13px] text-fg-subtle">
                    {user.email ?? "No email"} · {user.competitionsCount}{" "}
                    {user.competitionsCount === 1 ? "competition" : "competitions"}
                  </span>
                </span>
              </button>

              <span className="hidden text-[13px] text-fg-faint lg:block">
                Joined {formatKickoff(user.createdAt)}
              </span>

              <Button
                size="sm"
                variant={user.isSuperAdmin ? "secondary" : "ghost"}
                loading={toggleSuperAdmin.isPending && toggleSuperAdmin.variables?.id === user.id}
                onClick={() =>
                  toggleSuperAdmin.mutate({ id: user.id, grant: !user.isSuperAdmin })
                }
              >
                {user.isSuperAdmin ? "Revoke admin" : "Make admin"}
              </Button>
            </div>
          ))}
        </div>
      )}

      <Pager offset={offset} total={data?.total ?? 0} onChange={setOffset} />

      {toggleSuperAdmin.error && (
        <ErrorNote>
          {toggleSuperAdmin.error instanceof ApiError
            ? toggleSuperAdmin.error.message
            : "Couldn't update super admin access."}
        </ErrorNote>
      )}

      {detailId && <UserDetailModal userId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function UserDetailModal({ userId, onClose }: { userId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: user, isLoading } = useQuery({
    queryKey: ["admin", "user", userId],
    queryFn: () => api.admin.user(userId),
  });

  const [displayName, setDisplayName] = useState("");
  const [saved, setSaved] = useState(false);

  const update = useMutation({
    mutationFn: () => api.admin.updateUser(userId, { displayName: displayName.trim() }),
    onSuccess: () => {
      setSaved(true);
      setDisplayName("");
      void queryClient.invalidateQueries({ queryKey: ["admin", "user", userId] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      window.setTimeout(() => setSaved(false), 2500);
    },
    onError: (error) => trackApiError(error, "admin_rename_user"),
  });

  return (
    <Modal open onClose={onClose} title={user?.displayName ?? "User"} width="max-w-xl">
      {isLoading || !user ? (
        <div className="space-y-3">
          <Skeleton className="h-16" />
          <Skeleton className="h-20" />
          <Skeleton className="h-32" />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <Avatar name={user.displayName} color={user.avatarColor} size={48} />
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 font-semibold text-fg">
                {user.displayName}
                {user.isSuperAdmin && <Badge tone="warn">Super admin</Badge>}
              </p>
              <p className="truncate text-[13px] text-fg-subtle">
                {user.email ?? "No email on file"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="surface-soft p-3">
              <p className="label mb-1">Joined</p>
              <p className="text-[13px] text-fg-muted">{formatKickoff(user.createdAt)}</p>
            </div>
            <div className="surface-soft p-3">
              <p className="label mb-1">Last seen</p>
              <p className="text-[13px] text-fg-muted">
                {user.lastSeenAt ? formatRelative(user.lastSeenAt) : "Never"}
              </p>
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (displayName.trim().length >= 2) update.mutate();
            }}
            className="flex items-end gap-2"
          >
            <div className="flex-1">
              <Input
                label="Rename this user"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={user.displayName}
                minLength={2}
                maxLength={40}
                hint="For fixing an offensive or confusing name. They can change it back."
              />
            </div>
            <Button
              type="submit"
              loading={update.isPending}
              disabled={displayName.trim().length < 2}
            >
              Save
            </Button>
          </form>

          {update.error && (
            <ErrorNote>
              {update.error instanceof ApiError
                ? update.error.message
                : "Couldn't rename that user."}
            </ErrorNote>
          )}
          {saved && <SuccessNote>Display name updated.</SuccessNote>}

          <div>
            <p className="label mb-2.5">Competitions · {user.competitions.length}</p>
            {user.competitions.length === 0 ? (
              <p className="text-sm text-fg-subtle">Not entered into anything yet.</p>
            ) : (
              <div className="space-y-1.5">
                {user.competitions.map((c) => (
                  <Link
                    key={c.id}
                    to={`/c/${c.slug}`}
                    onClick={onClose}
                    className="flex items-center gap-2.5 rounded-xl border border-line px-3 py-2.5 text-[13px] transition-colors hover:border-line-strong hover:bg-tint"
                  >
                    <span className="min-w-0 flex-1 truncate font-medium text-fg">{c.name}</span>
                    {c.isOwner && <Badge tone="warn">Owner</Badge>}
                    <Badge tone={c.entryStatus === "alive" ? "alive" : "out"}>
                      {c.entryStatus}
                    </Badge>
                    <Badge tone="neutral">{c.status}</Badge>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/* Competitions ------------------------------------------------------------- */

function CompetitionsPanel() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "draft" | "running" | "complete">("");
  const [offset, setOffset] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "competitions", search, status, offset],
    queryFn: () =>
      api.admin.allCompetitions({
        search: search || undefined,
        status: status || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOffset(0);
          }}
          placeholder="Search by name or slug…"
          icon="search"
          aria-label="Search competitions"
          className="max-w-sm flex-1"
        />
        {/* Sized by a wrapper rather than a `w-auto` override — the field's own `w-full` and an
            override class have equal specificity, so which wins would depend on stylesheet order. */}
        <div className="w-full sm:w-48">
          <Select
            value={status}
            aria-label="Status"
            onChange={(e) => {
              setStatus(e.target.value as typeof status);
              setOffset(0);
            }}
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="running">Running</option>
            <option value="complete">Complete</option>
          </Select>
        </div>
        {data && (
          <p className="pb-3.5 text-[13px] text-fg-subtle">
            {data.total} {data.total === 1 ? "competition" : "competitions"}
          </p>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : !data?.competitions.length ? (
        <EmptyState title="No competitions found" description="Try a different filter." />
      ) : (
        <div className="card divide-y divide-line overflow-hidden">
          {data.competitions.map((c) => (
            <Link
              key={c.id}
              to={`/c/${c.slug}`}
              className="flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-tint"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-fg">{c.name}</span>
                <span className="block truncate text-[13px] text-fg-subtle">
                  {c.leagueName} · {c.seasonLabel} · owned by {c.ownerDisplayName}
                </span>
              </span>
              <span className="tabular hidden text-[13px] text-fg-subtle sm:block">
                {c.playersAlive}/{c.playersTotal} alive
              </span>
              <Badge tone={c.isJoinable ? "alive" : "neutral"}>
                {c.isJoinable ? "Open" : "Closed"}
              </Badge>
              <Badge
                tone={c.status === "running" ? "alive" : c.status === "complete" ? "neutral" : "info"}
              >
                {c.status}
              </Badge>
              <Icon name="chevronRight" className="size-4 text-fg-faint" />
            </Link>
          ))}
        </div>
      )}

      <Pager offset={offset} total={data?.total ?? 0} onChange={setOffset} />
    </div>
  );
}

/* Sync --------------------------------------------------------------------- */

/**
 * Which leagues the platform actually syncs.
 *
 * An inactive league is skipped by every sync and can't have a competition created in it, but
 * nothing already running in it breaks — which is why this is a toggle rather than a delete.
 * Each active league costs a slice of every sync's subrequest budget, so this is the lever for
 * "we only play the Premier League" as much as it is for adding one back later.
 */
function LeaguesCard() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: leagues, isLoading } = useQuery({
    queryKey: ["admin", "leagues"],
    queryFn: () => api.admin.allLeagues(),
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.admin.updateLeague(id, { isActive }),
    onSuccess: (_result, { isActive }) => {
      toast.show(isActive ? "League activated" : "League paused");
      void queryClient.invalidateQueries({ queryKey: ["admin", "leagues"] });
      void queryClient.invalidateQueries({ queryKey: ["leagues"] });
    },
    onError: (error) => {
      trackApiError(error, "admin_league_toggle");
      toast.show(error instanceof ApiError ? error.message : "Couldn't update that league", "error");
    },
  });

  if (isLoading) return <Skeleton className="h-40" />;
  if (!leagues?.length) return null;

  const active = leagues.filter((l) => l.isActive).length;

  return (
    <section>
      <h2 className="mb-1 text-sm font-semibold text-fg">Leagues</h2>
      <p className="mb-3 text-sm leading-relaxed text-fg-subtle">
        {active} of {leagues.length} active. Only active leagues are synced or offered when
        someone creates a competition; competitions already running in a paused league carry on
        untouched.
      </p>
      <div className="card divide-y divide-line overflow-hidden">
        {leagues.map((league) => {
          const fixtures = league.seasons.reduce((sum, s) => sum + s.fixtureCount, 0);
          return (
            <div key={league.id} className="flex items-center justify-between gap-3 p-4">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-fg">{league.name}</p>
                <p className="tabular mt-0.5 text-xs text-fg-faint">
                  {league.providerSlug} · {fixtures} fixture{fixtures === 1 ? "" : "s"}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge tone={league.isActive ? "alive" : "neutral"}>
                  {league.isActive ? "Active" : "Paused"}
                </Badge>
                <Button
                  variant={league.isActive ? "secondary" : "primary"}
                  size="sm"
                  disabled={toggle.isPending}
                  onClick={() => toggle.mutate({ id: league.id, isActive: !league.isActive })}
                >
                  {league.isActive ? "Pause" : "Activate"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** When results are pulled. Both modes stay well inside the database's free tier. */
function PollSchedulePanel() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({
    queryKey: ["admin", "pollSchedule"],
    queryFn: () => api.admin.pollSchedule(),
  });

  const save = useMutation({
    mutationFn: api.admin.savePollSchedule,
    onSuccess: (next) => {
      queryClient.setQueryData(["admin", "pollSchedule"], next);
      toast.show("Refresh schedule saved");
    },
    onError: (error) => {
      trackApiError(error, "save_poll_schedule");
      toast.show(error instanceof ApiError ? error.message : "Couldn't save", "error");
    },
  });

  if (!data) return <Skeleton className="h-40" />;

  return (
    <div className="card p-5">
      <h2 className="display-title text-xl text-fg">Results refresh</h2>
      <p className="mb-4 mt-1.5 text-sm leading-relaxed text-fg-subtle">
        {data.mode === "after_matches"
          ? "Pulls scores 15 minutes after full time — one refresh per kick-off slot, so all the 3pm games are done together."
          : "Pulls scores once, at the chosen time (UK), on days that have a match. Days without a match cost nothing."}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl
          value={data.mode}
          onChange={(mode) => save.mutate({ mode, dailyHour: data.dailyHour })}
          options={[
            { value: "after_matches", label: "After each match" },
            { value: "daily", label: "End of match day" },
          ]}
        />
        {data.mode === "daily" && (
          <Select
            aria-label="Time of day"
            value={String(data.dailyHour)}
            disabled={save.isPending}
            onChange={(e) => save.mutate({ mode: "daily", dailyHour: Number(e.target.value) })}
          >
            {Array.from({ length: 24 }, (_, hour) => (
              <option key={hour} value={hour}>
                {String(hour).padStart(2, "0")}:00
              </option>
            ))}
          </Select>
        )}
      </div>
      {data.mode === "after_matches" && data.nextDueAt && (
        <p className="mt-3 text-sm text-fg-muted">Next refresh: {formatKickoff(data.nextDueAt)}</p>
      )}
    </div>
  );
}

function SyncPanel() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [message, setMessage] = useState<string | null>(null);

  const { data: runs, isLoading } = useQuery({
    queryKey: ["admin", "syncRuns"],
    queryFn: () => api.admin.syncRuns(30),
    refetchInterval: 30_000,
  });

  const { data: leagues } = useQuery({ queryKey: ["leagues"], queryFn: () => api.leagues.list() });

  const sync = useMutation({
    mutationFn: (leagueId?: string) => api.admin.sync(leagueId ? { leagueId } : {}),
    onSuccess: (outcome, leagueId) => {
      const { leagues: summaries, remaining, budgetExhausted } = outcome;
      const created = summaries.reduce((sum, s) => sum + s.created, 0);
      const updated = summaries.reduce((sum, s) => sum + s.updated, 0);
      const errors = summaries.flatMap((s) => s.errors);
      analytics.track(AnalyticsEvent.AdminSyncTriggered, {
        leagueId: leagueId ?? "all",
        created,
        updated,
        errorCount: errors.length,
        remaining,
      });

      /**
       * `remaining` is not an error, and saying so matters. A sync of every league is bounded
       * by what one Worker invocation can afford, so "3 of 7" is the normal, successful
       * outcome — the admin just needs to know another press continues rather than repeats.
       */
      setMessage(
        `Synced ${summaries.length} league(s): ${created} created, ${updated} updated` +
          (errors.length ? `, ${errors.length} error(s) — open them under Recent runs` : "") +
          (remaining
            ? `. ${remaining} league(s) still to go — press again to continue.`
            : "."),
      );
      toast.show(
        errors.length
          ? `Synced with ${errors.length} error(s)`
          : budgetExhausted
            ? "Synced as far as this run could go"
            : remaining
              ? `Synced ${summaries.length}, ${remaining} to go`
              : "Sync complete",
      );
      queryClient.invalidateQueries({ queryKey: ["admin", "syncRuns"] });
    },
    onError: (error) => {
      trackApiError(error, "admin_sync");
      setMessage(error instanceof ApiError ? error.message : "Sync failed.");
      toast.show("Sync failed", "error");
    },
  });

  return (
    <div className="space-y-6">
      <PollSchedulePanel />

      <div className="card p-5">
        <h2 className="display-title text-xl text-fg">Force a sync</h2>
        <p className="mb-4 mt-1.5 text-sm leading-relaxed text-fg-subtle">
          Fixtures refresh weekly (Mondays, 04:00 UTC); results follow the schedule above. Use this when
          you can't wait, or when a provider was down. Syncing every league covers a few per
          press — a single run can't afford them all — so press again to work through the rest.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => sync.mutate(undefined)} loading={sync.isPending}>
            <Icon name="refresh" className="size-4" /> Sync all leagues
          </Button>
          {leagues?.slice(0, 4).map((league) => (
            <Button
              key={league.id}
              variant="secondary"
              size="sm"
              onClick={() => sync.mutate(league.id)}
              disabled={sync.isPending}
            >
              {league.name}
            </Button>
          ))}
        </div>
        {message && <p className="mt-4 text-sm text-fg-muted">{message}</p>}
      </div>

      <LeaguesCard />

      <section>
        <h2 className="mb-3 text-sm font-semibold text-fg">Recent runs</h2>
        {isLoading ? (
          <Skeleton className="h-48" />
        ) : !runs?.length ? (
          <EmptyState title="No sync runs yet" />
        ) : (
          <div className="card divide-y divide-line overflow-hidden">
            {runs.map((run) => (
              <div key={run.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <Badge
                  tone={
                    run.status === "success"
                      ? "alive"
                      : run.status === "failed"
                        ? "out"
                        : run.status === "partial"
                          ? "warn"
                          : "info"
                  }
                >
                  {run.status}
                </Badge>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-fg">
                    {run.leagueName ?? "All leagues"}
                    {run.providerKey && (
                      <span className="ml-1.5 font-normal text-fg-subtle">
                        via {run.providerKey}
                      </span>
                    )}
                    {run.usedFallback && (
                      <Badge tone="warn" className="ml-2">
                        Fallback
                      </Badge>
                    )}
                  </span>
                  <span className="block text-[13px] text-fg-subtle">
                    {formatKickoff(run.startedAt)} · {run.trigger}
                  </span>
                </span>
                <span className="tabular text-[13px] text-fg-subtle">
                  +{run.fixturesCreated} / ~{run.fixturesUpdated}
                </span>
                {run.errors.length > 0 && (
                  <details className="w-full text-[13px]">
                    <summary className="cursor-pointer text-danger-text">
                      {run.errors.length} error{run.errors.length === 1 ? "" : "s"}
                    </summary>
                    <ul className="mt-2 space-y-1.5">
                      {run.errors.map((error, i) => (
                        <li
                          key={i}
                          className="whitespace-pre-wrap break-words rounded-lg bg-surface-sunken px-3 py-2 font-mono text-xs text-fg-muted"
                        >
                          {typeof error === "string" ? error : JSON.stringify(error)}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* Providers ---------------------------------------------------------------- */

function ProvidersPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "providers"],
    queryFn: () => api.admin.providers(),
  });

  const [testing, setTesting] = useState<string | null>(null);
  const [slug, setSlug] = useState("eng.1");
  const [result, setResult] = useState<{ ok: boolean; count: number; error?: string } | null>(null);

  const test = useMutation({
    mutationFn: (id: string) => api.admin.testProvider(id, { slug }),
    onSuccess: (res) => setResult(res),
    onError: (error) => trackApiError(error, "test_provider"),
  });

  if (isLoading) return <Skeleton className="h-48" />;
  if (!data) return null;

  return (
    <div className="space-y-6">
      <div className="card p-5">
        <h2 className="display-title text-xl text-fg">Adapters</h2>
        <p className="mb-4 mt-1.5 text-sm leading-relaxed text-fg-subtle">
          A{" "}
          <code className="rounded bg-tint-strong px-1.5 py-0.5 text-[13px] text-fg-muted">
            generic_json
          </code>{" "}
          provider can be added from here with no deploy — its field mapping lives in the config.
          Anything more unusual gets a code adapter.
        </p>
        <div className="flex flex-wrap gap-2">
          {data.adapters.map((adapter) => (
            <Badge key={adapter} tone="info">
              {adapter}
            </Badge>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {data.providers.map((provider) => (
          <div key={provider.id} className="card p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-fg">{provider.name}</span>
                <span className="block text-[13px] text-fg-subtle">
                  {provider.key} · {provider.adapter} · priority {provider.priority}
                </span>
              </span>
              <Badge tone={provider.isActive ? "alive" : "neutral"}>
                {provider.isActive ? "Active" : "Disabled"}
              </Badge>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setTesting(testing === provider.id ? null : provider.id);
                  setResult(null);
                }}
              >
                Test
              </Button>
            </div>

            {testing === provider.id && (
              <div className="animate-fade mt-4 border-t border-line pt-4">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="w-full sm:w-48">
                    <Input
                      label="League slug"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder="eng.1"
                    />
                  </div>
                  <Button onClick={() => test.mutate(provider.id)} loading={test.isPending}>
                    Run test
                  </Button>
                </div>

                {result && (
                  <p
                    className={cx(
                      "mt-3 text-sm",
                      result.ok ? "text-accent-text" : "text-danger-text",
                    )}
                  >
                    {result.ok
                      ? `Parsed ${result.count} fixtures successfully.`
                      : `Failed: ${result.error}`}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* Import ------------------------------------------------------------------- */

function ImportPanel() {
  const [seasonId, setSeasonId] = useState("");
  const [payload, setPayload] = useState("");
  const [format, setFormat] = useState<"csv" | "json">("csv");

  const { data: leagues } = useQuery({ queryKey: ["leagues"], queryFn: () => api.leagues.list() });

  const preview = useMutation({
    mutationFn: () => api.admin.importPreview({ seasonId, format, payload }),
    onError: (error) => trackApiError(error, "import_preview"),
  });
  const apply = useMutation({
    mutationFn: () => api.admin.importApply({ seasonId, format, payload }),
    onError: (error) => trackApiError(error, "import_apply"),
  });

  return (
    <div className="space-y-6">
      <div className="card p-5">
        <h2 className="display-title text-xl text-fg">Import fixtures</h2>
        <p className="mb-5 mt-1.5 text-sm leading-relaxed text-fg-subtle">
          Always previewed first. Imported values count as manual edits, so a later sync won't
          overwrite them.
        </p>

        <div className="space-y-4">
          <Select
            label="Season"
            value={seasonId}
            onChange={(e) => setSeasonId(e.target.value)}
          >
            <option value="">Choose a season…</option>
            {leagues
              ?.filter((l) => l.currentSeason)
              .map((league) => (
                <option key={league.id} value={league.currentSeason!.id}>
                  {league.name} — {league.currentSeason!.label}
                </option>
              ))}
          </Select>

          <div>
            <p className="mb-1.5 text-sm font-medium text-fg-muted">Format</p>
            <SegmentedControl
              value={format}
              onChange={setFormat}
              options={[
                { value: "csv", label: "CSV" },
                { value: "json", label: "JSON" },
              ]}
            />
          </div>

          <Textarea
            label={format === "csv" ? "CSV" : "JSON array"}
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            rows={8}
            spellCheck={false}
            className="font-mono leading-relaxed"
            placeholder={
              format === "csv"
                ? "kickoff_at,home_team,away_team,home_score,away_score,status\n2026-08-15T14:00:00Z,Arsenal,Chelsea,2,1,finished"
                : '[{"kickoff_at":"2026-08-15T14:00:00Z","home_team":"Arsenal","away_team":"Chelsea"}]'
            }
          />

          <div className="grid grid-cols-2 gap-2 sm:flex">
            <Button
              variant="secondary"
              onClick={() => preview.mutate()}
              loading={preview.isPending}
              disabled={!seasonId || !payload}
            >
              Preview changes
            </Button>
            <Button
              onClick={() => apply.mutate()}
              loading={apply.isPending}
              disabled={!preview.data || !seasonId}
            >
              Apply import
            </Button>
          </div>

          {(preview.error || apply.error) && (
            <ErrorNote>
              {(preview.error ?? apply.error) instanceof ApiError
                ? ((preview.error ?? apply.error) as ApiError).message
                : "Import failed."}
            </ErrorNote>
          )}
        </div>
      </div>

      {(preview.data || apply.data) && (
        <ImportResult result={apply.data ?? preview.data!} applied={Boolean(apply.data)} />
      )}
    </div>
  );
}

function ImportResult({
  result,
  applied,
}: {
  result: NonNullable<Awaited<ReturnType<typeof api.admin.importPreview>>>;
  applied: boolean;
}) {
  return (
    <div className="card p-5">
      <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <h2 className="display-title text-xl text-fg">
          {applied ? "Import applied" : "Preview"}
        </h2>
        <div className="flex flex-wrap gap-2">
          <Badge tone="alive">{result.created} new</Badge>
          <Badge tone="warn">{result.updated} updated</Badge>
          <Badge tone="neutral">{result.unchanged} unchanged</Badge>
          {result.unmatched.length > 0 && (
            <Badge tone="out">{result.unmatched.length} unmatched</Badge>
          )}
        </div>
      </div>

      {result.unmatched.length > 0 && (
        <div className="mb-4 rounded-xl border border-danger-line bg-danger-soft p-3.5">
          <p className="mb-2 text-sm font-medium text-danger-text">Unmatched rows</p>
          <ul className="space-y-1 text-[13px] text-fg-muted">
            {result.unmatched.slice(0, 8).map((row, index) => (
              <li key={index}>{row.reason}</li>
            ))}
          </ul>
        </div>
      )}

      {result.changes.length > 0 && (
        <div className="mobile-scroll-tabs max-h-80 space-y-1.5 overflow-y-auto">
          {result.changes.map((change, index) => (
            <div key={index} className="rounded-lg border border-line px-3 py-2 text-[13px]">
              <p className="font-medium text-fg">{change.description}</p>
              <p className="text-fg-subtle">
                {change.before ? (
                  <>
                    {JSON.stringify(change.before)} → {JSON.stringify(change.after)}
                  </>
                ) : (
                  "New fixture"
                )}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Audit -------------------------------------------------------------------- */

function AuditPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "audit"],
    queryFn: () => api.admin.audit(100),
  });

  if (isLoading) return <Skeleton className="h-64" />;
  if (!data?.length) return <EmptyState title="Nothing logged yet" />;

  return (
    <div className="space-y-4">
      <InfoNote>
        Every privileged action lands here, including a super admin's own — that's what makes an
        override defensible when a player asks why they went out.
      </InfoNote>

      <div className="card divide-y divide-line overflow-hidden">
        {data.map((entry) => (
          <div
            key={entry.id}
            className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-3"
          >
            <code className="rounded bg-tint-strong px-1.5 py-0.5 text-[12px] font-medium text-fg-muted">
              {entry.action}
            </code>
            <span className="text-sm text-fg-muted">{entry.actor?.displayName ?? "system"}</span>
            {entry.reason && (
              <span className="text-[13px] italic text-fg-subtle">“{entry.reason}”</span>
            )}
            <span
              className="ml-auto text-[13px] text-fg-faint"
              title={formatKickoff(entry.createdAt)}
            >
              {formatRelative(entry.createdAt)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
